/**
 * How a node of the Memory control is drawn (D340): the glyph of each layer, the glow, the hex frame and its icon, the
 * orbit marker, the sparkle and the labels. Canvas 2D, no React; the painter (`MemoryBrainCanvas.tsx`) calls these.
 *
 * Ported from RUBRIC Agentic OS v2 (Jay E, RoboNuggets, MIT; see THIRD-PARTY-NOTICES.md): brain/index.html `drawNode`
 * (an application is a hexagon with a wash of its colour, an outline and its icon; a routine an orbit ring with a moon
 * and a core; a skill a four-point spark; a bubble a lit orb with a tilted ellipse round it and its count inside once it
 * is big enough; the selection two soft rings), brain/_flows2.js `glowSprite` / `orbSprite` / `hex` / `icon`, and
 * brain/_icons.js (the line icons on a 48 by 48 grid, here drawn from SVG path data). Our colours (the tier colours and
 * the department tones), our five outer layers get glyphs of their own so no layer is told apart by colour alone.
 */
import { hexToRgb, mixHex } from './memoryGlobeColours.js';
import type { GlobeLayer } from './memoryGlobeModel.js';

const TAU = Math.PI * 2;

export function rgba(hex: string, a: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r},${g},${b},${a})`;
}

/* ── Cached sprites ──────────────────────────────────────────────────── */

const glowCache = new Map<string, HTMLCanvasElement>();
/** A soft radial glow of a colour (`glowSprite`): 64 px, bright at the middle, nothing at the edge. */
export function glowSprite(colour: string): HTMLCanvasElement {
  const hit = glowCache.get(colour);
  if (hit) return hit;
  const S = 64;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const g = c.getContext('2d')!;
  const grd = g.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  grd.addColorStop(0, rgba(colour, 0.55));
  grd.addColorStop(0.4, rgba(colour, 0.22));
  grd.addColorStop(1, rgba(colour, 0));
  g.fillStyle = grd;
  g.fillRect(0, 0, S, S);
  glowCache.set(colour, c);
  return c;
}

const orbCache = new Map<string, HTMLCanvasElement>();
/** A lit sphere of a colour (`orbSprite`): 48 px, a highlight up and to the left. */
export function orbSprite(colour: string, lit = 0.5): HTMLCanvasElement {
  const key = `${colour}|${lit}`;
  const hit = orbCache.get(key);
  if (hit) return hit;
  const S = 48;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const g = c.getContext('2d')!;
  const grd = g.createRadialGradient(S * 0.38, S * 0.38, S * 0.05, S / 2, S / 2, S / 2);
  grd.addColorStop(0, mixHex(colour, '#ffffff', lit));
  grd.addColorStop(1, colour);
  g.fillStyle = grd;
  g.beginPath();
  g.arc(S / 2, S / 2, S / 2, 0, TAU);
  g.fill();
  orbCache.set(key, c);
  return c;
}

/* ── Shapes ──────────────────────────────────────────────────────────── */

/** A hexagon, point up to the sides as the source's `hex`. */
export function hexPath(ctx: CanvasRenderingContext2D, x: number, y: number, s: number): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i - Math.PI / 6;
    if (i) ctx.lineTo(x + s * Math.cos(a), y + s * Math.sin(a));
    else ctx.moveTo(x + s * Math.cos(a), y + s * Math.sin(a));
  }
  ctx.closePath();
}

/** A four-point spark (the source's skill): curved sides pinched to the middle. */
export function sparkPath(ctx: CanvasRenderingContext2D, x: number, y: number, s: number): void {
  ctx.beginPath();
  ctx.moveTo(x, y - s);
  ctx.quadraticCurveTo(x + s * 0.18, y - s * 0.18, x + s, y);
  ctx.quadraticCurveTo(x + s * 0.18, y + s * 0.18, x, y + s);
  ctx.quadraticCurveTo(x - s * 0.18, y + s * 0.18, x - s, y);
  ctx.quadraticCurveTo(x - s * 0.18, y - s * 0.18, x, y - s);
  ctx.closePath();
}

function polygonPath(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, sides: number, rot = -Math.PI / 2): void {
  ctx.beginPath();
  for (let i = 0; i < sides; i++) {
    const a = rot + (i / sides) * TAU;
    if (i) ctx.lineTo(x + s * Math.cos(a), y + s * Math.sin(a));
    else ctx.moveTo(x + s * Math.cos(a), y + s * Math.sin(a));
  }
  ctx.closePath();
}

function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, r: number): void {
  ctx.beginPath();
  ctx.roundRect(x - s, y - s, s * 2, s * 2, r);
}

/** A page with a folded corner: the Library's glyph. */
function pagePath(ctx: CanvasRenderingContext2D, x: number, y: number, s: number): void {
  const w = s * 0.82;
  const f = s * 0.45;
  ctx.beginPath();
  ctx.moveTo(x - w, y - s);
  ctx.lineTo(x + w - f, y - s);
  ctx.lineTo(x + w, y - s + f);
  ctx.lineTo(x + w, y + s);
  ctx.lineTo(x - w, y + s);
  ctx.closePath();
}

/** The outer layers' own shapes: a diamond for the roadmap, a block for the architecture, a pentagon for plans, a triangle for governance, a page for the library. */
export function layerShapePath(ctx: CanvasRenderingContext2D, layer: GlobeLayer, x: number, y: number, s: number): void {
  switch (layer) {
    case 'roadmap':
      polygonPath(ctx, x, y, s * 1.15, 4, 0);
      break;
    case 'architecture':
      roundRectPath(ctx, x, y, s * 0.9, s * 0.25);
      break;
    case 'plans':
      polygonPath(ctx, x, y, s * 1.05, 5);
      break;
    case 'decisions':
      polygonPath(ctx, x, y + s * 0.08, s * 1.15, 3);
      break;
    case 'library':
      pagePath(ctx, x, y, s * 0.95);
      break;
    default:
      ctx.beginPath();
      ctx.arc(x, y, s, 0, TAU);
  }
}

/* ── Icons ───────────────────────────────────────────────────────────── */

/** Line icons on a 48 by 48 grid, as `brain/_icons.js`: a hub of connected nodes for anything without an icon of its own. */
const ICON_PATHS: Record<string, string> = {
  hub: 'M24 18a6 6 0 1 0 0 12 6 6 0 0 0 0-12ZM9 5a4 4 0 1 0 0 8 4 4 0 0 0 0-8ZM39 5a4 4 0 1 0 0 8 4 4 0 0 0 0-8ZM9 35a4 4 0 1 0 0 8 4 4 0 0 0 0-8ZM39 35a4 4 0 1 0 0 8 4 4 0 0 0 0-8ZM12 12l8 8M28 28l8 8M12 36l8-8M28 20l8-8',
  connector: 'M17 5v11M31 5v11M11 16h26v8a13 13 0 0 1-26 0ZM24 37v7',
  plugin: 'M9 17h9a5 5 0 1 1 10 0h11v9a5 5 0 1 0 0 10v7H9V36a5 5 0 1 0 0-10Z',
  'built-in': 'M8 8h13v13H8ZM27 8h13v13H27ZM8 27h13v13H8ZM27 27h13v13H27Z',
  local: 'M7 9h34v24H7ZM17 41h14M24 33v8',
  wsl: 'M7 9h34v24H7ZM17 41h14M24 33v8',
  command: 'M6 10h36v28H6ZM13 19l7 5-7 5M24 30h11',
  skill: 'M24 4l4 14 14 6-14 6-4 14-4-14-14-6 14-6Z',
};

const pathCache = new Map<string, Path2D>();
function iconPath(kind: string): Path2D {
  const d = ICON_PATHS[kind] ?? ICON_PATHS.hub;
  let p = pathCache.get(d);
  if (!p) pathCache.set(d, (p = new Path2D(d)));
  return p;
}

/** One of the line icons, centred on (x, y) at `size` px across, stroked in `colour`. */
export function drawIcon(ctx: CanvasRenderingContext2D, kind: string | undefined, x: number, y: number, size: number, colour: string): void {
  ctx.save();
  ctx.translate(x - size / 2, y - size / 2);
  ctx.scale(size / 48, size / 48);
  ctx.strokeStyle = colour;
  ctx.lineWidth = 3.2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke(iconPath(kind ?? 'hub'));
  ctx.restore();
}

/** The icon a tool wears: its kind's own, or the hub. */
export function appIconKind(glyph: string | undefined): string {
  return glyph && ICON_PATHS[glyph] ? glyph : 'hub';
}

/* ── Nodes ───────────────────────────────────────────────────────────── */

export interface NodeStyle {
  layer: GlobeLayer;
  colour: string;
  x: number;
  y: number;
  /** Radius in pixels. */
  r: number;
  alpha: number;
  glyph?: string;
  /** Time in seconds, for the pulse and the moon. */
  t: number;
  /** A stable fraction of the node's name, so neighbours do not pulse together. */
  seed: number;
  /** A bubble: a memory node that weighs enough to be drawn big, with its weight for the count. */
  weight: number;
  disabled: boolean;
  glow: boolean;
  /** Draw the count inside and the orbit marker (radius on screen big enough). */
  big: boolean;
  /** Motion on: the moon travels, the sparkle twinkles. */
  motion: boolean;
}

export interface StructStyle extends NodeStyle {
  struct: 'layer' | 'group' | 'dir';
  /** A folder that is open. */
  open: boolean;
  /** What it holds, written inside it. */
  count: number;
}

/**
 * A hub or a folder: the layer's hub a glowing ring, a group's hub a lit orb, a folder a bubble that is a dashed ring when
 * open and a filled disc when closed. The count of what it holds is written inside, as the source draws `n.files` in a directory.
 */
export function drawStruct(ctx: CanvasRenderingContext2D, n: StructStyle): void {
  const { x, y, r, colour } = n;
  ctx.globalAlpha = n.alpha;
  if (n.struct === 'layer') {
    ctx.drawImage(glowSprite(colour), x - r * 2.8, y - r * 2.8, r * 5.6, r * 5.6);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    ctx.fillStyle = rgba(colour, 0.22);
    ctx.fill();
    ctx.strokeStyle = colour;
    ctx.lineWidth = Math.max(1.4, r * 0.1);
    ctx.stroke();
  } else if (n.struct === 'group') {
    ctx.drawImage(glowSprite(colour), x - r * 2.6, y - r * 2.6, r * 5.2, r * 5.2);
    ctx.drawImage(orbSprite(colour, 0.45), x - r, y - r, r * 2, r * 2);
    ctx.strokeStyle = rgba('#ffffff', 0.35);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(x, y, r + 2.5, 0, TAU);
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    ctx.fillStyle = rgba(colour, n.open ? 0.1 : 0.34);
    ctx.fill();
    ctx.strokeStyle = colour;
    ctx.lineWidth = Math.max(1.2, r * 0.09);
    if (n.open) ctx.setLineDash([Math.max(2, r * 0.3), Math.max(2, r * 0.22)]);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  if (r >= 7) {
    ctx.fillStyle = n.struct === 'group' ? 'rgba(5,6,13,0.9)' : '#eaf0ff';
    ctx.font = `700 ${Math.max(8, Math.min(16, r * 0.66))}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(Math.round(n.count)), x, y + 0.5);
    ctx.textBaseline = 'alphabetic';
  }
  ctx.globalAlpha = 1;
}

/** An application: a hexagon washed with its colour, outlined, with its icon inside. */
export function drawApp(ctx: CanvasRenderingContext2D, n: NodeStyle): void {
  const { x, y, r, colour } = n;
  ctx.globalAlpha = n.alpha;
  if (n.glow) ctx.drawImage(glowSprite(colour), x - r * 2.6, y - r * 2.6, r * 5.2, r * 5.2);
  hexPath(ctx, x, y, r);
  ctx.fillStyle = rgba(colour, n.disabled ? 0.08 : 0.2);
  ctx.fill();
  ctx.strokeStyle = n.disabled ? rgba(colour, 0.5) : colour;
  ctx.lineWidth = Math.max(1, r * 0.075);
  ctx.stroke();
  if (r >= 7) drawIcon(ctx, appIconKind(n.glyph), x, y, r * 1.12, n.disabled ? 'rgba(234,240,255,0.55)' : '#eaf0ff');
  ctx.globalAlpha = 1;
}

/** A routine: an orbit ring with a moon travelling it, and a core. */
export function drawRoutine(ctx: CanvasRenderingContext2D, n: NodeStyle): void {
  const { x, y, r, colour } = n;
  ctx.globalAlpha = n.alpha;
  if (n.glow) ctx.drawImage(glowSprite(colour), x - r * 2.4, y - r * 2.4, r * 4.8, r * 4.8);
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.strokeStyle = rgba(colour, n.disabled ? 0.4 : 0.85);
  ctx.lineWidth = Math.max(1, r * 0.09);
  ctx.stroke();
  const ma = (n.motion ? n.t * 0.9 : 0) + n.seed * TAU;
  ctx.fillStyle = colour;
  ctx.beginPath();
  ctx.arc(x + Math.cos(ma) * r, y + Math.sin(ma) * r, Math.max(1.4, r * 0.16), 0, TAU);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x, y, Math.max(1.5, r * 0.3), 0, TAU);
  ctx.fill();
  ctx.globalAlpha = 1;
}

/** A skill: a four-point spark that twinkles a little when things move. */
export function drawSkill(ctx: CanvasRenderingContext2D, n: NodeStyle): void {
  const tw = 1 + (n.motion ? 0.22 * Math.sin(n.t * 2.4 + n.seed * 13) : 0);
  const s = n.r * 1.25 * tw;
  ctx.globalAlpha = n.alpha;
  if (n.glow) ctx.drawImage(glowSprite(n.colour), n.x - s * 2.2, n.y - s * 2.2, s * 4.4, s * 4.4);
  sparkPath(ctx, n.x, n.y, s);
  ctx.fillStyle = n.disabled ? rgba(n.colour, 0.55) : n.colour;
  ctx.fill();
  ctx.globalAlpha = 1;
}

/** One of the five outer layers: a small shape of its own, washed and outlined like a hexagon but quieter. */
export function drawOuter(ctx: CanvasRenderingContext2D, n: NodeStyle): void {
  ctx.globalAlpha = n.alpha;
  if (n.glow) ctx.drawImage(glowSprite(n.colour), n.x - n.r * 2.4, n.y - n.r * 2.4, n.r * 4.8, n.r * 4.8);
  layerShapePath(ctx, n.layer, n.x, n.y, n.r);
  ctx.fillStyle = rgba(n.colour, n.disabled ? 0.1 : 0.26);
  ctx.fill();
  ctx.strokeStyle = n.disabled ? rgba(n.colour, 0.5) : n.colour;
  ctx.lineWidth = Math.max(1, n.r * 0.12);
  ctx.stroke();
  ctx.globalAlpha = 1;
}

/** A memory node that weighs enough: a lit orb with its orbit marker (a tilted ellipse) and, when big, its count. */
export function drawBubble(ctx: CanvasRenderingContext2D, n: NodeStyle): void {
  const { x, y, r, colour } = n;
  ctx.globalAlpha = n.alpha;
  if (n.glow) ctx.drawImage(glowSprite(colour), x - r * 2.6, y - r * 2.6, r * 5.2, r * 5.2);
  ctx.drawImage(orbSprite(colour, 0.5), x - r, y - r, r * 2, r * 2);
  if (n.big || r > 4) {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(-0.5);
    ctx.strokeStyle = rgba(colour, 0.55);
    ctx.lineWidth = Math.max(0.8, r * 0.09);
    ctx.beginPath();
    ctx.ellipse(0, 0, r * 1.65, r * 0.5, 0, 0, TAU);
    ctx.stroke();
    ctx.restore();
  }
  if (r >= 9) {
    ctx.fillStyle = 'rgba(5,6,13,0.9)';
    ctx.font = `700 ${Math.max(7, r * 0.62)}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(Math.round(n.weight)), x, y + 0.5);
    ctx.textBaseline = 'alphabetic';
  }
  ctx.globalAlpha = 1;
}

/** The selection: two soft rings that breathe (`drawSelection`), in the accent. */
export function drawSelection(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, accent: string, colour: string, t: number, motion: boolean): void {
  const p = motion ? Math.sin(t * 4.5) : 0;
  ctx.strokeStyle = 'rgba(255,255,255,0.8)';
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.arc(x, y, r + 5 + p * 1.5, 0, TAU);
  ctx.stroke();
  ctx.strokeStyle = rgba(accent, 0.6);
  ctx.beginPath();
  ctx.arc(x, y, r + 10 + p * 2.5, 0, TAU);
  ctx.stroke();
  ctx.strokeStyle = rgba(colour, 0.35);
  ctx.beginPath();
  ctx.arc(x, y, r + 16 + p * 3, 0, TAU);
  ctx.stroke();
}

/** A layer's label: spaced capitals in the layer's colour with a soft glow of it and a dark outline, as the source's MEMORY / SKILLS. */
export function drawLayerLabel(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, colour: string, opts: { align?: CanvasTextAlign; alpha?: number; size?: number; hot?: boolean } = {}): { w: number; h: number } {
  const size = opts.size ?? 11;
  ctx.save();
  ctx.globalAlpha = opts.alpha ?? 0.92;
  ctx.font = `600 ${size}px Inter, system-ui, sans-serif`;
  try {
    ctx.letterSpacing = `${(size * 0.22).toFixed(1)}px`;
  } catch {
    // An older engine draws it unspaced.
  }
  ctx.textAlign = opts.align ?? 'center';
  ctx.lineJoin = 'round';
  const label = text.toUpperCase();
  const w = ctx.measureText(label).width;
  ctx.strokeStyle = 'rgba(0,0,0,0.85)';
  ctx.lineWidth = 4;
  ctx.strokeText(label, x, y);
  ctx.shadowColor = colour;
  ctx.shadowBlur = opts.hot ? 18 : 11;
  ctx.fillStyle = colour;
  ctx.fillText(label, x, y);
  ctx.restore();
  return { w, h: size };
}

/** A node's name: plain text with a dark halo, so it reads over anything. */
export function drawNameLabel(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, opts: { colour?: string; size?: number; alpha?: number; bold?: boolean } = {}): number {
  ctx.save();
  ctx.globalAlpha = opts.alpha ?? 1;
  ctx.font = `${opts.bold ? 600 : 400} ${opts.size ?? 11}px Inter, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = 'rgba(0,0,0,0.85)';
  ctx.lineWidth = 3.5;
  ctx.strokeText(text, x, y);
  ctx.fillStyle = opts.colour ?? '#e6e9ee';
  ctx.fillText(text, x, y);
  const w = ctx.measureText(text).width;
  ctx.restore();
  return w;
}

/** A quadratic bow between two points, as `linkCtrl`: stable per link index so the same link always curves the same way. */
export function bowControl(ax: number, ay: number, bx: number, by: number, i: number): [number, number] {
  const sign = (i * 2654435761) % 2 ? 1 : -1;
  const bow = sign * (0.07 + (((i * 97) % 13) / 13) * 0.09);
  return [(ax + bx) / 2 - (by - ay) * bow, (ay + by) / 2 + (bx - ax) * bow];
}
